import useExtendedFormContext from '@components/form-hook-wrapper/hooks/useExtendedFormContext';
import RadioGroupComponent from '@components/input/radio-group/RadioGroup';
import { Radio } from '@mis/sushi-tailwind-react';
import { FieldController } from '@model/field-controller';
import { RadioOption } from '@model/radio';
import React from 'react';
import { Controller, FieldPath, FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export type RadioGroupProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = FieldController<TFieldValues, TName> &
  React.ComponentProps<typeof Radio> & {
    items: RadioOption[];
    ['data-testid']?: string;
    viewOnly?: boolean;
  };

const RadioGroup = <TFieldValues extends FieldValues = FieldValues, TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>>({
  name,
  rules,
  items,
  viewOnly,
  onChange,
  ...props
}: RadioGroupProps<TFieldValues, TName>): React.ReactElement => {
  const { t } = useTranslation(['translation', 'error', 'label']);
  const { control, displayErrorMessage } = useExtendedFormContext<TFieldValues>();

  const onRadioChange = (onchange: (...event: AnyValue[]) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
    onchange(e);
    onChange && onChange(e);
  };

  return (
    <Controller
      name={name}
      rules={rules}
      control={control}
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      render={({ field: { ref, ...field }, fieldState: { error } }) => {
        return (
          <RadioGroupComponent
            {...field}
            {...props}
            items={items}
            name={name}
            viewOnly={viewOnly}
            status={displayErrorMessage && error ? 'error' : undefined}
            message={displayErrorMessage && error ? t(error?.message as string) : undefined}
            onChange={onRadioChange(field.onChange)}
          />
        );
      }}
    />
  );
};

export default RadioGroup;
